import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import { PrintHeader } from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";

const PAYMENT_METHOD_KEYS = [
    { key: "cash", label: "Cash" },
    { key: "transfer", label: "Transfer" },
    { key: "qris", label: "QRIS" },
    { key: "30_hari", label: "30 Hari" },
    { key: "60_hari", label: "60 Hari" },
    { key: "90_hari", label: "90 Hari" },
    { key: "giro", label: "Giro" },
];

const MASTER_CASH_KEYS = [
    { key: "kas_besar", label: "Kas Besar" },
    { key: "bca", label: "BCA" },
    { key: "kas_kecil", label: "Kas Kecil" },
    { key: "mandiri", label: "Mandiri" },
    { key: "bri", label: "BRI" },
];

const CashRecapReportPrint = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const params = {
        start_date: searchParams.get("start_date") || "",
        end_date: searchParams.get("end_date") || "",
        customer_id: searchParams.get("customer_id") || undefined,
    };

    const { data, isLoading, error } = useFetchData(
        "report/cash-recap",
        params,
    );

    const salesData = data?.sales || [];
    const creditData = data?.credit_payments || [];
    const period = data?.period || {};

    const salesTotals = PAYMENT_METHOD_KEYS.reduce((acc, { key }) => {
        acc[key] = salesData.reduce((sum, row) => sum + (row[key] || 0), 0);
        return acc;
    }, {});
    salesTotals.total = salesData.reduce(
        (sum, row) => sum + (row.total || 0),
        0,
    );

    // Filter out payment method columns where all values are 0
    const activeSalesCols = PAYMENT_METHOD_KEYS.filter(({ key }) => salesTotals[key] > 0);

    const creditTotals = MASTER_CASH_KEYS.reduce((acc, { key }) => {
        acc[key] = creditData.reduce((sum, row) => sum + (row[key] || 0), 0);
        return acc;
    }, {});
    creditTotals.total = creditData.reduce(
        (sum, row) => sum + (row.total || 0),
        0,
    );

    // Filter out master cash columns where all values are 0
    const activeCreditCols = MASTER_CASH_KEYS.filter(({ key }) => creditTotals[key] > 0);

    const periodDisplay =
        period.start === period.end
            ? formatDate(period.start)
            : `${formatDate(period.start)} - ${formatDate(period.end)}`;

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/cash-recap-report")}
                >
                    Kembali
                </Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-[210mm] text-black">
            {/* Action bar */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/cash-recap-report")}
                >
                    Kembali
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title = `Rekap_Kas_${period.start || ""}`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print content */}
            <div className="p-8 print:p-0">
                <PrintHeader title="REKAP KAS" />

                <div className="mb-2 text-xs">
                    <span className="font-medium">Periode: </span>
                    {periodDisplay}
                </div>

                {/* PENJUALAN */}
                {salesData.length > 0 && (
                    <>
                        <div className="mb-2 text-sm font-bold">Penjualan</div>
                        <table className={`mb-6 w-full border-collapse border border-black ${activeSalesCols.length >= 6 ? "text-[10px]" : "text-[11px]"}`}>
                            <thead>
                                <tr className="bg-gray-100 whitespace-nowrap">
                                    <th className="border border-black px-1 py-px text-center">
                                        No.
                                    </th>
                                    <th className="border border-black px-1 py-px text-left">
                                        Customer
                                    </th>
                                    {activeSalesCols.map(
                                        ({ key, label }) => (
                                            <th
                                                key={key}
                                                className="border border-black px-1 py-px text-right"
                                            >
                                                {label}
                                            </th>
                                        ),
                                    )}
                                    <th className="border border-black px-1 py-px text-right">
                                        Total
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {salesData.map((row, idx) => (
                                    <tr key={row.customer_id} className="whitespace-nowrap">
                                        <td className="border border-black px-1 py-px text-center">
                                            {idx + 1}.
                                        </td>
                                        <td className="border border-black px-1 py-px">
                                            {row.customer_name}
                                        </td>
                                        {activeSalesCols.map(({ key }) => (
                                            <td
                                                key={key}
                                                className="border border-black px-1 py-px text-right"
                                            >
                                                {row[key]
                                                    ? formatCurrency(row[key])
                                                    : "-"}
                                            </td>
                                        ))}
                                        <td className="border border-black px-1 py-px text-right font-bold">
                                            {formatCurrency(row.total)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 font-bold">
                                    <td
                                        colSpan={2}
                                        className="border border-black px-1 py-px text-center"
                                    >
                                        TOTAL
                                    </td>
                                    {activeSalesCols.map(({ key }) => (
                                        <td
                                            key={key}
                                            className="border border-black px-1 py-px text-right"
                                        >
                                            {salesTotals[key]
                                                ? formatCurrency(
                                                      salesTotals[key],
                                                  )
                                                : "-"}
                                        </td>
                                    ))}
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatCurrency(salesTotals.total)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                {/* PEMBAYARAN KREDIT */}
                {creditData.length > 0 && (
                    <>
                        <div className="mb-2 text-sm font-bold">
                            Pembayaran Kredit
                        </div>
                        <table className={`mb-6 w-full border-collapse border border-black ${activeCreditCols.length >= 4 ? "text-[10px]" : "text-[11px]"}`}>
                            <thead>
                                <tr className="bg-gray-100 whitespace-nowrap">
                                    <th className="border border-black px-1 py-px text-center">
                                        No.
                                    </th>
                                    <th className="border border-black px-1 py-px text-left">
                                        Customer
                                    </th>
                                    {activeCreditCols.map(({ key, label }) => (
                                        <th
                                            key={key}
                                            className="border border-black px-1 py-px text-right"
                                        >
                                            {label}
                                        </th>
                                    ))}
                                    <th className="border border-black px-1 py-px text-right">
                                        Total
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {creditData.map((row, idx) => (
                                    <tr key={row.customer_id} className="whitespace-nowrap">
                                        <td className="border border-black px-1 py-px text-center">
                                            {idx + 1}.
                                        </td>
                                        <td className="border border-black px-1 py-px">
                                            {row.customer_name}
                                        </td>
                                        {activeCreditCols.map(({ key }) => (
                                            <td
                                                key={key}
                                                className="border border-black px-1 py-px text-right"
                                            >
                                                {row[key]
                                                    ? formatCurrency(row[key])
                                                    : "-"}
                                            </td>
                                        ))}
                                        <td className="border border-black px-1 py-px text-right font-bold">
                                            {formatCurrency(row.total)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 font-bold">
                                    <td
                                        colSpan={2}
                                        className="border border-black px-1 py-px text-center"
                                    >
                                        TOTAL
                                    </td>
                                    {activeCreditCols.map(({ key }) => (
                                        <td
                                            key={key}
                                            className="border border-black px-1 py-px text-right"
                                        >
                                            {creditTotals[key]
                                                ? formatCurrency(
                                                      creditTotals[key],
                                                  )
                                                : "-"}
                                        </td>
                                    ))}
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatCurrency(creditTotals.total)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                {/* Footer */}
                <div className="mt-8 text-xs text-gray-500">
                    <p>
                        Dicetak pada:{" "}
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CashRecapReportPrint;
